import { Box, SxProps, Theme, Typography } from "@mui/material";
import useTheme from "@mui/material/styles/useTheme";
import {
  CheckBadgeFilledIcon,
  CheckCircleIcon,
  ClockFilledCircleIcon,
  ClockFilledIcon,
  CloseIcon,
  HammerIcon,
  MergeIcon,
  ReopenIcon,
  UpArrow2Icon,
  UserFilledIcon,
} from "components/icons";
import { convertStringToSentenceCase } from "utilz/helpers";
import { BadgeBase, BadgeProps } from "./BadgeBase";

/**
 * One row of the status lookup table.
 *
 * Declared rather than inferred for `backgroundMode`: written inline, TS widens
 * `"light"` to `string`, which `BadgeProps` rejects. The array was previously
 * read through an `any`, so the mismatch went unnoticed.
 */
interface StatusBadgeEntry {
  /** Absent on the fallback entry, which is matched by neither id nor name. */
  id?: string;
  value: string;
  /**
   * Typed as `BadgeProps` does — optional — because several theme accessors the
   * entries use are themselves optional: every step on `PrimitiveColor` is,
   * as are `warning.tint` / `success.tint`. Requiring a plain `string` here
   * would reject the tokens this table is built from.
   */
  backgroundColor: BadgeProps["backgroundColor"];
  backgroundMode: BadgeProps["backgroundMode"];
  borderColor?: string;
  textColor?: string;
  icon: JSX.Element;
}

interface StatusProps extends BadgeProps {
  value?: string;
  id?: string;
  /**
   * Full status name used for the LOOKUP only, when `value` has already been
   * shortened for display.
   *
   * Tables truncate the label in JS to keep a column narrow, which destroys the
   * name match — and the name is the only route for a status whose per-entity
   * binding the backend forked per MSP (`Waiting on Customer` in project and
   * task views). Pass the untruncated `status_name` here and the badge resolves
   * correctly while still rendering the short `value`.
   */
  lookupValue?: string;
  containerStyles?: SxProps<Theme>;
}
export const StatusBadge = ({
  id,
  value: inputValue,
  lookupValue,
  containerStyles,
  ...rest
}: StatusProps) => {
  const theme = useTheme();
  const defaultData: StatusBadgeEntry = {
    value: inputValue || "New",
    backgroundColor: "#E8F3FE",
    backgroundMode: "light",
    borderColor: theme.palette.purple.hover,
    textColor: theme.palette.purple.hover,
    icon: (
      <CheckBadgeFilledIcon
        fontSize="small"
        htmlColor={theme.palette.purple.hover}
      />
    ),
  };
  const StatusBadgeArray: StatusBadgeEntry[] = [
    {
      id: "c302d5e0-176c-4396-9f2c-e6742af81a43",
      value: "Pending",
      backgroundColor: theme.palette.warning.tint,
      borderColor: theme.palette.warning.main,
      textColor: "#D35800",
      backgroundMode: "dark",
      icon: <ClockFilledIcon fontSize="small" htmlColor="#D35800" />,
    },
    {
      id: "789c8882-2f6b-495c-ad91-3c9bcbbcc3c7",
      value: "Assigned",
      backgroundColor: "#E7E9FE",
      borderColor: "#907BFF",
      textColor: "#907BFF",
      backgroundMode: "dark",
      icon: <UserFilledIcon fontSize="small" htmlColor="#907BFF" />,
    },
    {
      id: "e246583c-8714-4c90-a7cc-e3e2d576e780",
      value: "In Progress",
      backgroundColor: "#E7E9FE",
      borderColor: theme.palette.purple.hover,
      textColor: theme.palette.purple.hover,
      backgroundMode: "dark",
      icon: (
        <HammerIcon
          sx={{ fontSize: "14px" }}
          htmlColor={theme.palette.purple.hover}
        />
      ),
    },
    {
      id: "da5be1f9-db9d-4c64-8e43-51b458746773",
      value: "Closed",
      backgroundColor: theme.palette.success.tint,
      borderColor: theme.palette.success.main,
      textColor: theme.palette.success.main,
      backgroundMode: "dark",
      icon: (
        <CheckCircleIcon
          sx={{ fontSize: "16px" }}
          htmlColor={theme.palette.success.main}
        />
      ),
    },
    {
      id: "9dd09ea5-e30e-494a-9120-583caff52a3a",
      value: "New",
      backgroundColor: "#E8F3FE",
      backgroundMode: "light",
      borderColor: "#0075C8",
      textColor: "#0075C8",
      icon: <CheckBadgeFilledIcon fontSize="small" htmlColor="#0075C8" />,
    },
    {
      id: "caf3b9ab-08a5-4014-938c-aacc021f043b",
      value: "Merged",
      backgroundColor: theme.palette.primitive.system.cyan["50"],
      backgroundMode: "light",
      icon: (
        <MergeIcon
          sx={{ fontSize: "14px" }}
          htmlColor={theme.palette.primitive.system.cyan["600"]}
        />
      ),
    },
    {
      id: "953b24ef-2167-49f9-88d9-a06cca165753",
      value: "Cancelled",
      // red.50 (#FFEBEA) stands in for the spec's #FFEEED — a 3-unit delta on
      // one channel, imperceptible, and it keeps the chip on a token.
      backgroundColor: theme.palette.primitive.system.red["50"],
      backgroundMode: "light",
      icon: (
        <CloseIcon
          sx={{ fontSize: "16px" }}
          htmlColor={theme.palette.primitive.system.red["600"]}
        />
      ),
    },
    {
      id: "30f434a9-4e66-4bea-86e3-86bb945035b3",
      value: "Reopened",
      backgroundColor: theme.palette.primitive.system.gold["50"],
      backgroundMode: "light",
      icon: (
        <ReopenIcon
          sx={{ fontSize: "16px" }}
          htmlColor={theme.palette.primitive.system.gold["600"]}
        />
      ),
    },
    {
      // = `statusData.WAITING_ON_CUSTOMER` (shared/data.ts). Written as a
      // literal like the nine entries above rather than importing the enum, so
      // the array stays uniform.
      //
      // Confirmed against the live status payload: this is the TICKET binding
      // and the row-level id. The project/task bindings carry a DIFFERENT,
      // MSP-scoped uuid, which is why the name fall-through above still earns
      // its place — it catches those.
      //
      // The name stays exact-case: UAT has an MSP-created `Waiting on customer`
      // beside the built-in, and that row keeps the neutral default badge.
      id: "f3a6c8c2-7c1f-4de6-bad3-9d013b9d4e76",
      value: "Waiting on Customer",
      backgroundColor: theme.palette.primitive.system.amber["50"],
      backgroundMode: "light",
      icon: (
        <ClockFilledCircleIcon
          sx={{ fontSize: "16px" }}
          htmlColor={theme.palette.primitive.system.amber["800"]}
          handColor={theme.palette.neutral.main}
        />
      ),
    },
    {
      id: "b8f2e1d4-3a7c-4f9e-b6d5-2c1a0e8f7b3a",
      value: "Escalated",
      backgroundColor: theme.palette.primitive.system.red["200"],
      backgroundMode: "light",
      icon: (
        <Box
          sx={{
            // Explicit and square: the disc must not size itself off the icon,
            // or `borderRadius: 50%` yields an ellipse and the arrow touches the
            // rim. 16 against a 14px glyph leaves a 1px ring.
            width: "16px",
            height: "16px",
            borderRadius: "50%",
            backgroundColor: theme.palette.primitive.system.red["800"],
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
            mr: "4px", // without this margin it feels like gap between icon and label is lesser for escalated status badge compared to other status badges (this happened because there is more whitespace within the other icons)
          }}
        >
          <UpArrow2Icon
            sx={{ fontSize: "14px" }}
            htmlColor={theme.palette.neutral.main}
          />
        </Box>
      ),
    },
  ];

  // Id first, then the name — and the name is tried even when an id WAS
  // supplied but didn't match. That fall-through is what catches a status whose
  // id this array cannot know: the backend forks a default's per-entity binding
  // PER MSP, so `Waiting on Customer` carries one uuid for tickets (above) and a
  // tenant-specific one for projects and tasks. Mirrors `getStatusIcon` exactly.
  //
  // Consequence worth knowing: an MSP-created status NAMED like a built-in now
  // inherits that built-in's badge. That is visuals only — ownership and
  // permissions still key on `status_msp_id`, never on the name.
  //
  // Derived during render rather than mirrored into state. As a `useState` +
  // `useEffect` this rendered twice per lookup, and the effect's deps could not
  // honestly include `StatusBadgeArray` — it is rebuilt every render and closes
  // over `theme`, so on a light/dark switch the badge kept icons tinted from the
  // previous theme until `id` or `value` happened to change. Not memoised: the
  // array is rebuilt regardless, so a `useMemo` over a ten-item `find` would add
  // bookkeeping and save nothing.
  const byId = id ? StatusBadgeArray.find((item) => item.id === id) : undefined;
  // `lookupValue` first: it is the untruncated name when the caller shortened
  // `value` for display. Falls back to `value` for the callers that pass the
  // whole name already, which is most of them.
  const name = lookupValue || inputValue;
  const byValue = name
    ? StatusBadgeArray.find((item) => item.value === name)
    : undefined;
  const statusData = byId ?? byValue ?? defaultData;

  return (
    <BadgeBase
      {...rest}
      backgroundColor={statusData.backgroundColor}
      backgroundMode={statusData.backgroundMode}
      leadingIcon={statusData.icon}
      containerStyles={{
        // border: `1px solid ${statusData.borderColor}`,
        minWidth: "84px",
        justifyContent: "left",
        padding: "4px 12px 4px 8px",
        borderRadius: "4px",
        // No `...statusData.containerStyles` here: no entry in the table
        // defines that key, so it only ever spread `undefined`. It compiled
        // because the lookup result was typed `any`; the explicit entry type
        // above surfaced it as dead.
        ...containerStyles,
      }}
    >
      <Typography
        variant="caption-xs"
        sx={{
          color: `${theme.palette.lightBg.high} !important`,
          textTransform: "none",
        }}
      >
        {convertStringToSentenceCase(inputValue || statusData.value)}
      </Typography>
    </BadgeBase>
  );
};
